import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { ringSummary } from '@kraftwerk/shared';
import type { Ring } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { loadWeekRings } from '../db/achievements.js';
import type { WeekRingsView } from '../db/achievements.js';
import { useAchievementScope } from '../hooks/useModules.js';
import { useTimeZone } from '../hooks/useToday.js';

const RADIUS = { training: 40, protein: 30, supplements: 20 } as const;

/**
 * Drei Ringe für die laufende Woche (E13): Training, Protein, Supplements.
 *
 * Geschlossen heißt `good`, offen ist ein neutraler Strich — kein Rot, kein
 * Bernstein: Ein halb gefüllter Ring am Mittwoch ist kein Versäumnis. Ringe
 * ohne Ziel stehen als Satz da („keine Trainingstage geplant"), nicht als
 * leerer Kreis, der wie Versagen aussieht.
 */
export function WeekRings({
  size = 'small',
  onSetup,
}: {
  size?: 'small' | 'large';
  /**
   * Ring ohne Ziel → dorthin, wo das Ziel entsteht. Ohne diesen Weg wäre
   * „Kein Proteinziel gesetzt“ eine Feststellung ohne Ausweg.
   */
  onSetup?: ((ring: Ring['key']) => void) | undefined;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const timeZone = useTimeZone();
  // Module (E15) und Teen-Freigaben (28.10.2026): Training-Ring nur mit
  // Training, Protein/Supplements nur mit Ernährung — bei Teens nie.
  const scope = useAchievementScope();
  const view = useLiveQuery(
    () => (timeZone ? loadWeekRings(timeZone, db) : undefined),
    [timeZone],
  ) as WeekRingsView | undefined;
  if (!view || !view.enabled) return null;
  const rings = view.rings.filter((ring) => scope.rings.includes(ring.key));
  if (rings.length === 0) return null;

  const summary = ringSummary(rings);
  const label = (ring: Ring) =>
    ring.goal === null
      ? t(`rings.noGoal.${ring.key}`)
      : t(`rings.value.${ring.key}`, { done: ring.done, goal: ring.goal });

  return (
    <section
      aria-labelledby={`rings-heading-${size}`}
      className={`kw-tile flex items-center gap-4 ${size === 'large' ? 'flex-col sm:flex-row' : ''}`}
    >
      <svg
        viewBox="0 0 100 100"
        className={size === 'large' ? 'size-40 shrink-0' : 'size-20 shrink-0'}
        role="img"
        aria-label={t('rings.summary', { closed: summary.closed, total: summary.total })}
      >
        {rings.map((ring) => {
          const r = RADIUS[ring.key];
          const length = 2 * Math.PI * r;
          const share = ring.goal ? Math.min(1, ring.done / ring.goal) : 0;
          return (
            <g key={ring.key} transform="rotate(-90 50 50)">
              <circle
                cx="50"
                cy="50"
                r={r}
                fill="none"
                strokeWidth="8"
                style={{ stroke: 'var(--color-surface-raised)' }}
              />
              {ring.goal !== null && share > 0 ? (
                <circle
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${length * share} ${length}`}
                  className="kw-ring"
                  style={{ stroke: ring.closed ? 'var(--color-good)' : 'var(--color-ink-muted)' }}
                />
              ) : null}
            </g>
          );
        })}
      </svg>
      <div className="flex min-w-0 flex-col gap-1">
        <h2 id={`rings-heading-${size}`} className="kw-kicker">
          {t('rings.heading')}
        </h2>
        <ul className="flex flex-col gap-0.5 text-sm">
          {rings.map((ring) => (
            <li key={ring.key} className={ring.closed ? 'text-good' : ''}>
              {ring.goal === null && onSetup ? (
                <button
                  type="button"
                  onClick={() => onSetup(ring.key)}
                  className="-my-2 py-2 text-left text-accent underline underline-offset-2"
                >
                  {t(`rings.setup.${ring.key}`)}
                </button>
              ) : (
                <>
                  {ring.closed ? '✓ ' : ''}
                  {label(ring)}
                </>
              )}
            </li>
          ))}
        </ul>
        {summary.total > 0 && summary.closed === summary.total ? (
          <p className="kw-pop text-sm font-semibold text-good">{t('rings.allClosed')}</p>
        ) : null}
      </div>
    </section>
  );
}
